/**
 * mandate-registration-queries.ts - the TanStack Query hooks of the Brokermandat registration with the
 * insurers: the list of a mandate, the broker's decisions, the review and Zuordnen tasks and the admin
 * settings.
 *
 * Every decision refreshes the mandate's registration list, the customer's registration reads and
 * Mandatskacheln and the policies (an answer moves their registration status and courtage
 * eligibility), also after a failure (the api may have moved the row: a lost race, a failed send that
 * went back to «Zur Freigabe»).
 */

import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { REGISTRATION_EXCEPTION_CODE, REGISTRATION_MAX_SWEEPS } from '@brokly/shared';
import type {
  AblehnenRegistrationBody,
  ApplyRegistrationProposalBody,
  BestaetigenRegistrationBody,
  BrokerRegistrationSettingsDto,
  KorrekturRegistrationBody,
  RegistrationProposalDto,
  RegistrationZuordnenDto,
  ZuordnenRegistrationBody,
  RueckfrageBeantwortetRegistrationBody,
  RueckfrageRegistrationBody,
  RegistrationAltbestandDto,
  RegistrationDto,
  RegistrationJetztMeldenReason,
  RunRegistrationsResultDto,
  SetBrokerRegistrationSettingsBody,
} from '@brokly/shared';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';

/** How often the list refreshes while the send job runs, for the first REGISTRATION_SLOW_AFTER_MS of following. */
export const REGISTRATION_POLL_MS = 3000;
/** Then it refreshes this often: a send_pending row may wait hours for the hourly sweeps. */
export const REGISTRATION_SLOW_POLL_MS = 30_000;
export const REGISTRATION_SLOW_AFTER_MS = 2 * 60_000;
/** After this long the follow stops polling (a stuck job, a worker down) and the screen asks for a reload. */
export const REGISTRATION_FOLLOW_MAX_MS = 15 * 60_000;

/**
 * A row the send job still owes a result: claimed (sending) or waiting for the job (send_pending), unless
 * the sweeps gave the job up and a task took over.
 */
export function isRegistrationInFlight(r: Pick<RegistrationDto, 'status' | 'exception_codes' | 'sweep_count'>): boolean {
  if (r.status === 'sending') return true;
  return (
    r.status === 'zurueckgestellt' &&
    r.exception_codes.includes(REGISTRATION_EXCEPTION_CODE.SEND_PENDING) &&
    r.sweep_count < REGISTRATION_MAX_SWEEPS
  );
}

/** The longest a «Meldung auslösen» is watched for the rows its job plans; it stops once they have landed. */
export const RUN_WATCH_MS = 30_000;

/**
 * A watched run of one mandate: its deadline, the rows already in flight when it started, and whether a
 * row of it was seen in flight.
 */
interface RegistrationWatch {
  until: number;
  before: string[];
  sawInFlight: boolean;
}

/** The follow of a mandate's job: when it started (a run restarts it), and whether the cap stopped its polling. */
interface RegistrationFollow {
  since: number;
  stopped: boolean;
}

// Outside the ['registrations'] prefix, so no invalidation resets them.
const watchKey = (mandateId: string) => ['registration-watch', mandateId] as const;
const followKey = (mandateId: string) => ['registration-follow', mandateId] as const;
const followEndKey = (mandateId: string) => ['registration-follow-end', mandateId] as const;

/** «Meldung auslösen» went through: every screen of the mandate follows its job, from any of its buttons. */
export function startRegistrationWatch(qc: QueryClient, mandateId: string): void {
  const before = (qc.getQueryData<RegistrationDto[]>(qk.registrationList(mandateId)) ?? []).filter(isRegistrationInFlight).map((r) => r.id);
  const watch: RegistrationWatch = { until: Date.now() + RUN_WATCH_MS, before, sawInFlight: false };
  qc.setQueryData(watchKey(mandateId), watch);
  restartRegistrationFollow(qc, mandateId);
}

/** A decision that can put a row in flight: the mandate is followed afresh, also once the cap stopped an earlier follow. */
export function restartRegistrationFollow(qc: QueryClient, mandateId: string): void {
  const follow: RegistrationFollow = { since: Date.now(), stopped: false };
  qc.setQueryData(followKey(mandateId), follow);
}

/** A follow state of a mandate, null when none; a cache entry, so every screen of the mandate shares it. */
function useFollowState<T>(queryKey: readonly unknown[]): T | null {
  return useQuery<T | null>({
    queryKey,
    queryFn: () => null,
    enabled: false,
    initialData: null,
    staleTime: Infinity,
  }).data;
}

/**
 * A broker mandate's registrations. The list refreshes itself while a row is in flight, and while a run
 * is watched: right after «Meldung auslösen» the job may still have to plan the rows it then sends. Every
 * REGISTRATION_POLL_MS at first, then every REGISTRATION_SLOW_POLL_MS, until the cap stops the follow.
 */
export function useRegistrations(mandateId: string, opts: { enabled?: boolean } = {}) {
  const watching = useFollowState<RegistrationWatch>(watchKey(mandateId)) !== null;
  const follow = useFollowState<RegistrationFollow>(followKey(mandateId));
  return useQuery<RegistrationDto[]>({
    queryKey: qk.registrationList(mandateId),
    queryFn: () => apiClient.registrations.list(mandateId),
    enabled: (opts.enabled ?? true) && mandateId !== '',
    refetchInterval: (query) => {
      if (follow?.stopped || !(watching || query.state.data?.some(isRegistrationInFlight))) return false;
      return follow !== null && Date.now() - follow.since >= REGISTRATION_SLOW_AFTER_MS ? REGISTRATION_SLOW_POLL_MS : REGISTRATION_POLL_MS;
    },
  });
}

/**
 * Follows the send job of a broker mandate while a run is watched or a row is in flight: 'live' while
 * the list refreshes itself, 'stopped' once REGISTRATION_FOLLOW_MAX_MS of following stopped that, null
 * when nothing is followed. The watch ends at its deadline or once the rows it saw in flight have
 * landed; when following ends, the reads of the screen are refreshed once, by whichever of the mounted
 * followers sees it first.
 */
export function useFollowRegistrationJob(
  mandateId: string,
  subject: { subjectType: 'person' | 'firma'; subjectId: string },
  opts: { enabled?: boolean } = {},
): 'live' | 'stopped' | null {
  const qc = useQueryClient();
  const { subjectType, subjectId } = subject;
  const enabled = (opts.enabled ?? true) && mandateId !== '';
  const watch = useFollowState<RegistrationWatch>(watchKey(mandateId));
  const follow = useFollowState<RegistrationFollow>(followKey(mandateId));
  const list = useRegistrations(mandateId, { enabled });
  const inFlight = list.data?.some(isRegistrationInFlight) ?? false;
  // A row in flight before the run may land before the job has planned the run's rows: only the others count.
  const runInFlight = list.data?.some((r) => isRegistrationInFlight(r) && !watch?.before.includes(r.id)) ?? false;
  const busy = enabled && (watch !== null || inFlight);
  const wasBusy = useRef(false);

  useEffect(() => {
    if (!enabled || watch === null) return;
    if (runInFlight && !watch.sawInFlight) {
      const seen: RegistrationWatch = { ...watch, sawInFlight: true };
      qc.setQueryData(watchKey(mandateId), seen);
      return;
    }
    if (!runInFlight && watch.sawInFlight) {
      // The rows the job planned have all landed: no need to wait out the window.
      qc.setQueryData(watchKey(mandateId), null);
      return;
    }
    const t = setTimeout(() => qc.setQueryData(watchKey(mandateId), null), Math.max(0, watch.until - Date.now()));
    return () => clearTimeout(t);
  }, [qc, mandateId, enabled, watch, runInFlight]);

  useEffect(() => {
    if (!enabled) return;
    if (!busy) {
      // Also a clock left by a follow that ended while no screen watched it: the next follow starts afresh.
      if (follow !== null) qc.setQueryData(followKey(mandateId), null);
      return;
    }
    if (follow === null) {
      const started: RegistrationFollow = { since: Date.now(), stopped: false };
      qc.setQueryData(followKey(mandateId), started);
      return;
    }
    if (follow.stopped) return;
    const stopped: RegistrationFollow = { ...follow, stopped: true };
    const t = setTimeout(() => qc.setQueryData(followKey(mandateId), stopped), Math.max(0, follow.since + REGISTRATION_FOLLOW_MAX_MS - Date.now()));
    return () => clearTimeout(t);
  }, [qc, mandateId, enabled, busy, follow]);

  useEffect(() => {
    if (wasBusy.current && !busy) {
      // Every follower of the mandate sees the same end in the same render: refresh once.
      const last = qc.getQueryData<number>(followEndKey(mandateId)) ?? 0;
      if (Date.now() - last > 1000) {
        qc.setQueryData(followEndKey(mandateId), Date.now());
        invalidateRegistrationReads(qc, mandateId, { subjectType, subjectId });
      }
    }
    wasBusy.current = busy;
  }, [busy, qc, mandateId, subjectType, subjectId]);

  return busy ? (follow?.stopped ? 'stopped' : 'live') : null;
}

/** Every read a registration decision or a finished send can change. */
export function invalidateRegistrationReads(
  qc: QueryClient,
  mandateId: string,
  subject: { subjectType: 'person' | 'firma'; subjectId: string },
): void {
  void qc.invalidateQueries({ queryKey: qk.registrationList(mandateId) });
  void qc.invalidateQueries({ queryKey: qk.registrations(subject.subjectType, subject.subjectId) });
  // Every registration read, the Aufgaben peek's (task, proposal, Zuordnen) included.
  void qc.invalidateQueries({ queryKey: ['registrations'] });
  void qc.invalidateQueries({ queryKey: qk.registrationTile(subject.subjectType, subject.subjectId) });
  void qc.invalidateQueries({ queryKey: qk.mandateBadges(subject.subjectType, subject.subjectId) });
  void qc.invalidateQueries({ queryKey: ['policies'] });
  // A reminder or an escalation opens (or settles) tasks; an answer decision closes them.
  void qc.invalidateQueries({ queryKey: ['tasks'] });
  // The process card names the insurers still without a Meldung.
  void qc.invalidateQueries({ queryKey: qk.mandateProcessState(mandateId) });
}

export type RegistrationDecision =
  | { action: 'run' }
  | { action: 'freigeben'; registrationId: string }
  | { action: 'jetzt_melden'; registrationId: string; reasonCode: RegistrationJetztMeldenReason }
  | { action: 'schliessen'; registrationId: string; notiz: string }
  | { action: 'erneut_melden'; carrierUid: string }
  // The reminder loop: the one reminder now, the Klärung task now.
  | { action: 'erinnern'; registrationId: string }
  | { action: 'eskalieren'; registrationId: string }
  // A «Versand unklar» reminder resolved by a broker.
  | { action: 'versand_bestaetigt'; registrationId: string }
  | { action: 'nicht_versendet'; registrationId: string }
  // The insurer's answer, recorded by a broker.
  | { action: 'bestaetigen'; registrationId: string; body: BestaetigenRegistrationBody }
  | { action: 'ablehnen'; registrationId: string; body: AblehnenRegistrationBody }
  | { action: 'rueckfrage'; registrationId: string; body: RueckfrageRegistrationBody }
  | { action: 'rueckfrage_beantwortet'; registrationId: string; body: RueckfrageBeantwortetRegistrationBody }
  | { action: 'korrektur'; registrationId: string; body: KorrekturRegistrationBody };

/** One decision on the registrations of a mandate. */
export function useRegistrationDecision(mandateId: string, subject: { subjectType: 'person' | 'firma'; subjectId: string }) {
  const qc = useQueryClient();
  return useMutation<RegistrationDto | RunRegistrationsResultDto, Error, RegistrationDecision>({
    mutationFn: (d) => {
      switch (d.action) {
        case 'run':
          return apiClient.registrations.run(mandateId);
        case 'freigeben':
          return apiClient.registrations.freigeben(mandateId, d.registrationId);
        case 'jetzt_melden':
          return apiClient.registrations.jetztMelden(mandateId, d.registrationId, { reasonCode: d.reasonCode });
        case 'schliessen':
          return apiClient.registrations.schliessen(mandateId, d.registrationId, { notiz: d.notiz });
        case 'erneut_melden':
          return apiClient.registrations.erneutMelden(mandateId, { carrierUid: d.carrierUid });
        case 'erinnern':
          return apiClient.registrations.erinnern(mandateId, d.registrationId);
        case 'eskalieren':
          return apiClient.registrations.eskalieren(mandateId, d.registrationId);
        case 'versand_bestaetigt':
          return apiClient.registrations.versandBestaetigt(mandateId, d.registrationId);
        case 'nicht_versendet':
          return apiClient.registrations.nichtVersendet(mandateId, d.registrationId);
        case 'bestaetigen':
          return apiClient.registrations.bestaetigen(mandateId, d.registrationId, d.body);
        case 'ablehnen':
          return apiClient.registrations.ablehnen(mandateId, d.registrationId, d.body);
        case 'rueckfrage':
          return apiClient.registrations.rueckfrage(mandateId, d.registrationId, d.body);
        case 'rueckfrage_beantwortet':
          return apiClient.registrations.rueckfrageBeantwortet(mandateId, d.registrationId, d.body);
        case 'korrektur':
          return apiClient.registrations.korrektur(mandateId, d.registrationId, d.body);
        default: {
          const unhandled: never = d;
          throw new Error(`unhandled decision ${String(unhandled)}`);
        }
      }
    },
    onSuccess: (_res, d) => {
      if (d.action === 'run') startRegistrationWatch(qc, mandateId);
      else if (d.action === 'freigeben' || d.action === 'jetzt_melden' || d.action === 'erneut_melden') restartRegistrationFollow(qc, mandateId);
    },
    onSettled: () => invalidateRegistrationReads(qc, mandateId, subject),
  });
}

/** The registration a task of the loop is about (an escalation, a failed reminder); 404 for another task. */
export function useTaskRegistration(taskId: string) {
  return useQuery<RegistrationDto>({
    queryKey: qk.taskRegistration(taskId),
    queryFn: () => apiClient.registrations.taskRegistration(taskId),
    retry: retryTransient,
  });
}

/** The rows behind a task «Altbestand prüfen»; 404 for another task. */
export function useRegistrationAltbestand(taskId: string) {
  return useQuery<RegistrationAltbestandDto>({
    queryKey: qk.registrationAltbestand(taskId),
    queryFn: () => apiClient.registrations.altbestand(taskId),
    retry: retryTransient,
  });
}

/** Is this the api's 404 (the task has no reply, or is no Zuordnen task)? */
export function isNotFound(err: unknown): boolean {
  return (err as { status?: unknown } | null)?.status === 404;
}

/** Retry a transient failure once, never a refusal (4xx): a 404 is an answer here, not an outage. */
function retryTransient(failureCount: number, err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  return !(typeof status === 'number' && status >= 400 && status < 500) && failureCount < 1;
}

/**
 * The parsed insurer reply behind a decision task of the registration (the review, a contradictory
 * answer, ...). 404 for a task without a reply: the Aufgaben peek then shows no review section.
 */
export function useRegistrationProposal(taskId: string) {
  return useQuery<RegistrationProposalDto>({
    queryKey: qk.registrationProposal(taskId),
    queryFn: () => apiClient.registrations.proposal(taskId),
    retry: retryTransient,
  });
}

/** The candidate registrations of a Zuordnen task (a mail no registration could be matched to); 404 for another task. */
export function useRegistrationZuordnen(taskId: string) {
  return useQuery<RegistrationZuordnenDto>({
    queryKey: qk.registrationZuordnen(taskId),
    queryFn: () => apiClient.registrations.zuordnen(taskId),
    retry: retryTransient,
  });
}

/**
 * «Zuordnen»: match the mail of a Zuordnen task to one registration. The api closes the task and opens
 * a review task for that registration, so the tasks and every registration read refresh.
 */
export function useZuordnenRegistration(taskId: string) {
  const qc = useQueryClient();
  return useMutation<{ review_task_id: string }, Error, ZuordnenRegistrationBody>({
    mutationFn: (body) => apiClient.registrations.zuordnenApply(taskId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['registrations'] });
    },
  });
}

/**
 * Übernehmen / Korrigieren / Verwerfen of a review task. The api decides the registration and closes
 * the task, so the tasks, every registration read and the policies refresh (also after a failure: a
 * lost race may have decided it already).
 */
export function useApplyRegistrationProposal(taskId: string) {
  const qc = useQueryClient();
  return useMutation<RegistrationProposalDto, Error, ApplyRegistrationProposalBody>({
    mutationFn: (body) => apiClient.registrations.applyProposal(taskId, body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['registrations'] });
      void qc.invalidateQueries({ queryKey: ['policies'] });
    },
  });
}

/** The Brokermandat Meldung settings (Administration). */
export function useBrokerRegistrationSettings() {
  return useQuery<BrokerRegistrationSettingsDto>({
    queryKey: qk.adminBrokerRegistration(),
    queryFn: () => apiClient.admin.brokerRegistrationSettings(),
  });
}

/** Save the Brokermandat Meldung settings. */
export function useSetBrokerRegistrationSettings() {
  const qc = useQueryClient();
  return useMutation<BrokerRegistrationSettingsDto, Error, SetBrokerRegistrationSettingsBody>({
    mutationFn: (body) => apiClient.admin.setBrokerRegistrationSettings(body),
    onSuccess: (saved) => {
      qc.setQueryData(qk.adminBrokerRegistration(), saved);
    },
  });
}
